import { useEffect, useMemo, useRef, useState } from 'react';
import {
  deploymentsApi,
  type ClientDeployment,
  type DeploymentStatus,
  type OrgOption,
} from '../api/deployments';
import { IconServer, IconTrash, IconRefresh, IconPlus, IconAlertTriangle, IconMail } from '../components/admin/icons';

const IN_PROGRESS: DeploymentStatus[] = ['pending', 'provisioning', 'seeding', 'destroying'];

const DB_NODE_TYPES = [
  { value: 'DB-DEV-S', label: 'DB-DEV-S — 2 vCPU / 2 Go (dev)' },
  { value: 'DB-DEV-M', label: 'DB-DEV-M — 2 vCPU / 4 Go' },
  { value: 'DB-GP-XS', label: 'DB-GP-XS — 2 vCPU / 8 Go (prod)' },
];

const REGIONS = [
  { value: 'fr-par', label: 'Paris (fr-par)' },
  { value: 'nl-ams', label: 'Amsterdam (nl-ams)' },
  { value: 'pl-waw', label: 'Varsovie (pl-waw)' },
];

// Cible de déploiement. 'scaleway-zce' (Serverless), 'archive' (zip) et le push
// SSH (vps/linux) sont opérationnels ; les clouds client sont en préparation.
const TARGETS = [
  { value: 'scaleway-zce', label: 'Scaleway ZCE', ready: true },
  { value: 'archive', label: 'Archive téléchargeable', ready: true },
  { value: 'vps', label: 'VPS OVH (push SSH)', ready: true },
  { value: 'linux', label: 'Serveur Linux divers (push SSH)', ready: true },
  { value: 'gcp', label: 'Google Cloud (GCP)', ready: true },
  { value: 'scaleway-client', label: 'Scaleway (compte client)', ready: false },
  { value: 'aws', label: 'Amazon AWS', ready: false },
  { value: 'azure', label: 'Microsoft Azure', ready: false },
];

const SSH_TARGETS = ['vps', 'linux'];

// Types de VM GCP (coût = facturation du CLIENT). e2-medium = confort par défaut.
const GCP_MACHINE_TYPES = [
  { value: 'e2-small', label: 'e2-small — 2 vCPU / 2 Go (le moins cher)' },
  { value: 'e2-medium', label: 'e2-medium — 2 vCPU / 4 Go (recommandé)' },
  { value: 'e2-standard-2', label: 'e2-standard-2 — 2 vCPU / 8 Go' },
];

function StatusBadge({ status }: { status: DeploymentStatus }) {
  const map: Record<DeploymentStatus, { label: string; cls: string; pulse?: boolean }> = {
    pending: { label: 'En attente', cls: 'bg-slate-100 text-slate-600', pulse: true },
    provisioning: { label: 'Provisioning', cls: 'bg-blue-100 text-blue-700', pulse: true },
    seeding: { label: 'Import données', cls: 'bg-indigo-100 text-indigo-700', pulse: true },
    ready: { label: 'Opérationnel', cls: 'bg-emerald-100 text-emerald-700' },
    failed: { label: 'Échec', cls: 'bg-red-100 text-red-700' },
    destroying: { label: 'Suppression', cls: 'bg-amber-100 text-amber-700', pulse: true },
    destroyed: { label: 'Supprimé', cls: 'bg-slate-100 text-slate-400' },
  };
  const s = map[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.cls}`}>
      {s.pulse && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
      {s.label}
    </span>
  );
}

export default function AdminDeploymentsPage() {
  const [deployments, setDeployments] = useState<ClientDeployment[]>([]);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Formulaire
  const [orgId, setOrgId] = useState<number | ''>('');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [target, setTarget] = useState('scaleway-zce');
  const [region, setRegion] = useState('fr-par');
  const [dbNodeType, setDbNodeType] = useState('DB-DEV-S');
  const [submitting, setSubmitting] = useState(false);

  // Connexion SSH (cibles vps/linux) — identifiants ÉPHÉMÈRES (jamais stockés).
  const [sshHost, setSshHost] = useState('');
  const [sshPort, setSshPort] = useState('22');
  const [sshUser, setSshUser] = useState('root');
  const [httpPort, setHttpPort] = useState('80');
  const [sshKeyB64, setSshKeyB64] = useState('');
  const [sshKeyName, setSshKeyName] = useState('');
  const [sshKeyError, setSshKeyError] = useState<string | null>(null);

  // Cloud cible GCP (identifiants du client, ÉPHÉMÈRES — jamais stockés).
  const [gcpProject, setGcpProject] = useState('');
  const [gcpRegion, setGcpRegion] = useState('europe-west1');
  const [gcpZone, setGcpZone] = useState('europe-west1-b');
  const [machineType, setMachineType] = useState('e2-medium');
  const [gcpKeyB64, setGcpKeyB64] = useState('');
  const [gcpKeyName, setGcpKeyName] = useState('');
  const [gcpKeyError, setGcpKeyError] = useState<string | null>(null);

  const isSshTarget = SSH_TARGETS.includes(target);
  const isGcpTarget = target === 'gcp';

  // Suppression
  const [confirmDelete, setConfirmDelete] = useState<ClientDeployment | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Renvoi d'e-mail
  const [notifyingId, setNotifyingId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const pollRef = useRef<number | null>(null);

  const load = async () => {
    try {
      const [deps, os] = await Promise.all([
        deploymentsApi.list(),
        orgs.length ? Promise.resolve(orgs) : deploymentsApi.listOrganizations(),
      ]);
      setDeployments(deps);
      if (!orgs.length) setOrgs(os);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling tant qu'un déploiement est en cours.
  const hasInProgress = useMemo(
    () => deployments.some((d) => IN_PROGRESS.includes(d.status)),
    [deployments],
  );
  useEffect(() => {
    if (hasInProgress && pollRef.current == null) {
      pollRef.current = window.setInterval(() => {
        deploymentsApi.list().then(setDeployments).catch(() => {});
      }, 5000);
    }
    if (!hasInProgress && pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current != null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [hasInProgress]);

  // Orgs déjà déployées (actives) → exclues du sélecteur.
  const deployedOrgIds = useMemo(
    () => new Set(deployments.filter((d) => d.status !== 'destroyed' && d.status !== 'failed').map((d) => d.org_id)),
    [deployments],
  );
  const availableOrgs = orgs.filter((o) => !deployedOrgIds.has(o.id));

  // Lecture du fichier de clé privée → base64 (reste côté navigateur jusqu'à
  // l'envoi ; transmis au backend puis au job de façon éphémère, jamais stocké).
  const onKeyFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setSshKeyError(null);
    const file = e.target.files?.[0];
    if (!file) { setSshKeyB64(''); setSshKeyName(''); return; }
    if (file.size > 64 * 1024) {
      setSshKeyError('Fichier trop volumineux pour une clé privée.');
      setSshKeyB64(''); setSshKeyName('');
      return;
    }
    try {
      const text = await file.text();
      if (!/PRIVATE KEY/.test(text)) {
        setSshKeyError('Ce fichier ne ressemble pas à une clé privée (PEM/OpenSSH).');
        setSshKeyB64(''); setSshKeyName('');
        return;
      }
      setSshKeyB64(btoa(text));
      setSshKeyName(file.name);
    } catch {
      setSshKeyError('Lecture du fichier impossible.');
      setSshKeyB64(''); setSshKeyName('');
    }
  };

  // Lecture de la clé JSON du compte de service GCP → base64 (reste côté
  // navigateur jusqu'à l'envoi ; transmise au job de façon éphémère, jamais stockée).
  const onGcpKeyFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setGcpKeyError(null);
    const file = e.target.files?.[0];
    if (!file) { setGcpKeyB64(''); setGcpKeyName(''); return; }
    if (file.size > 64 * 1024) {
      setGcpKeyError('Fichier trop volumineux pour une clé de compte de service.');
      setGcpKeyB64(''); setGcpKeyName('');
      return;
    }
    try {
      const text = await file.text();
      const sa = JSON.parse(text);
      if (sa?.type !== 'service_account' || !sa?.private_key || !sa?.client_email) {
        setGcpKeyError('Ce fichier n’est pas une clé JSON de compte de service GCP.');
        setGcpKeyB64(''); setGcpKeyName('');
        return;
      }
      if (sa.project_id && !gcpProject) setGcpProject(String(sa.project_id));
      setGcpKeyB64(btoa(unescape(encodeURIComponent(text))));
      setGcpKeyName(file.name);
    } catch {
      setGcpKeyError('Lecture du fichier impossible (JSON invalide).');
      setGcpKeyB64(''); setGcpKeyName('');
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: Parameters<typeof deploymentsApi.create>[0] = {
        orgId: Number(orgId), notifyEmail: notifyEmail.trim(), target, region, dbNodeType,
      };
      if (isSshTarget) {
        payload.sshHost = sshHost.trim();
        payload.sshPort = Number(sshPort) || 22;
        payload.sshUser = sshUser.trim();
        payload.sshKeyB64 = sshKeyB64;
        payload.httpPort = Number(httpPort) || 80;
      }
      if (isGcpTarget) {
        payload.gcpCredentialsB64 = gcpKeyB64;
        payload.gcpProject = gcpProject.trim();
        payload.gcpRegion = gcpRegion.trim();
        payload.gcpZone = gcpZone.trim();
        payload.machineType = machineType;
        payload.httpPort = Number(httpPort) || 80;
      }
      await deploymentsApi.create(payload);
      setOrgId('');
      setNotifyEmail('');
      setSshKeyB64(''); setSshKeyName('');
      setGcpKeyB64(''); setGcpKeyName('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    setNotice(null);
    try {
      const r = await deploymentsApi.remove(confirmDelete.id);
      setConfirmDelete(null);
      if (r.note) setNotice(r.note);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const doNotify = async (d: ClientDeployment) => {
    setNotifyingId(d.id);
    setNotice(null);
    setError(null);
    try {
      await deploymentsApi.notify(d.id);
      setNotice(`E-mail envoyé pour ${d.org_name || d.slug}.`);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setNotifyingId(null);
    }
  };

  const doDownload = async (d: ClientDeployment) => {
    setError(null);
    try {
      const url = await deploymentsApi.archiveUrl(d.id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white flex items-center justify-center shadow-md">
          <IconServer className="w-6 h-6" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Déploiements client</h1>
          <p className="text-sm text-slate-500">Instances dédiées Scaleway (snapshot des données d'une organisation).</p>
        </div>
      </div>

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          <IconAlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {notice && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
          <IconMail className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{notice}</span>
        </div>
      )}

      {/* Formulaire de déploiement */}
      <form onSubmit={submit} className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <IconPlus className="w-4 h-4" /> Nouveau déploiement
        </h2>
        <div className="mb-4">
          <label className="block text-xs font-medium text-slate-500 mb-1">Cible de déploiement</label>
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            {TARGETS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}{t.ready ? '' : ' — bientôt'}</option>
            ))}
          </select>
          {target === 'archive' && (
            <p className="mt-1 text-xs text-slate-500">
              Génère une <strong>archive</strong> (setup.sh + image + vos données) à déployer sur votre hôte Docker. Téléchargement via un lien valable 1 h.
            </p>
          )}
          {isSshTarget && (
            <p className="mt-1 text-xs text-slate-500">
              ZCE se connecte en <strong>SSH</strong> au serveur et déploie l'instance (Docker + données) automatiquement. Les identifiants ci-dessous sont <strong>éphémères</strong> : utilisés pour ce déploiement, jamais conservés.
            </p>
          )}
          {isGcpTarget && (
            <p className="mt-1 text-xs text-slate-500">
              ZCE crée une <strong>VM Compute Engine</strong> sur le compte GCP <strong>du client</strong> et y déploie l'instance. La clé de compte de service est <strong>éphémère</strong> : utilisée pour ce déploiement, jamais conservée (donc la suppression retire le suivi — la VM se supprime dans la console GCP).
            </p>
          )}
          {['scaleway-client', 'aws', 'azure'].includes(target) && (
            <p className="mt-1 text-xs text-amber-600">
              Provisioning cloud automatique — en préparation (Phase 3).
            </p>
          )}
        </div>

        {/* Connexion SSH avancée — cibles VPS / serveur Linux. */}
        {isSshTarget && (
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold text-slate-600 mb-3">Connexion SSH (éphémère)</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Adresse (IP ou hôte)</label>
                <input
                  type="text"
                  value={sshHost}
                  onChange={(e) => setSshHost(e.target.value)}
                  placeholder="203.0.113.10"
                  required={isSshTarget}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Utilisateur</label>
                <input
                  type="text"
                  value={sshUser}
                  onChange={(e) => setSshUser(e.target.value)}
                  placeholder="root"
                  required={isSshTarget}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Port SSH</label>
                <input
                  type="number"
                  value={sshPort}
                  onChange={(e) => setSshPort(e.target.value)}
                  placeholder="22"
                  min={1}
                  max={65535}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <div className="md:col-span-3">
                <label className="block text-xs font-medium text-slate-500 mb-1">Fichier de clé privée</label>
                <input
                  type="file"
                  accept=".pem,.key,.ppk,.txt,application/octet-stream,text/plain"
                  onChange={onKeyFile}
                  className="w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                />
                {sshKeyName && !sshKeyError && (
                  <p className="mt-1 text-xs text-emerald-600">Clé chargée : {sshKeyName}</p>
                )}
                {sshKeyError && <p className="mt-1 text-xs text-red-600">{sshKeyError}</p>}
                <p className="mt-1 text-xs text-slate-400">
                  Clé OpenSSH/PEM sans phrase de passe. L'utilisateur doit être root (ou avoir sudo sans mot de passe).
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Port HTTP</label>
                <input
                  type="number"
                  value={httpPort}
                  onChange={(e) => setHttpPort(e.target.value)}
                  placeholder="80"
                  min={1}
                  max={65535}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Cloud cible GCP — VM sur le compte Google Cloud du client. */}
        {isGcpTarget && (
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold text-slate-600 mb-3">Compte GCP du client (éphémère)</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">ID du projet GCP</label>
                <input
                  type="text"
                  value={gcpProject}
                  onChange={(e) => setGcpProject(e.target.value)}
                  placeholder="mon-projet-client"
                  required={isGcpTarget}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Région</label>
                <input
                  type="text"
                  value={gcpRegion}
                  onChange={(e) => setGcpRegion(e.target.value)}
                  placeholder="europe-west1"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Zone</label>
                <input
                  type="text"
                  value={gcpZone}
                  onChange={(e) => setGcpZone(e.target.value)}
                  placeholder="europe-west1-b"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Type de VM</label>
                <select
                  value={machineType}
                  onChange={(e) => setMachineType(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  {GCP_MACHINE_TYPES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Clé JSON du compte de service</label>
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={onGcpKeyFile}
                  className="w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                />
                {gcpKeyName && !gcpKeyError && (
                  <p className="mt-1 text-xs text-emerald-600">Clé chargée : {gcpKeyName}</p>
                )}
                {gcpKeyError && <p className="mt-1 text-xs text-red-600">{gcpKeyError}</p>}
                <p className="mt-1 text-xs text-slate-400">
                  Compte de service avec le rôle « Administrateur Compute » sur le projet.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Port HTTP</label>
                <input
                  type="number"
                  value={httpPort}
                  onChange={(e) => setHttpPort(e.target.value)}
                  placeholder="80"
                  min={1}
                  max={65535}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-500 mb-1">Organisation</label>
            <select
              value={orgId}
              onChange={(e) => setOrgId(e.target.value ? Number(e.target.value) : '')}
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">— Sélectionner un client —</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.slug})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Région</label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {REGIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Base de données</label>
            <select
              value={dbNodeType}
              onChange={(e) => setDbNodeType(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {DB_NODE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-xs font-medium text-slate-500 mb-1">Email de l'utilisateur final</label>
          <input
            type="email"
            required
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
            placeholder="client@exemple.com"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
          <p className="mt-1 text-xs text-slate-400">
            Reçoit le lien vers l'application + un compte (avec mot de passe) pour s'y connecter.
          </p>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="submit"
            disabled={
              submitting || !orgId || !notifyEmail.trim() ||
              (isSshTarget && (!sshHost.trim() || !sshUser.trim() || !sshKeyB64)) ||
              (isGcpTarget && (!gcpProject.trim() || !gcpKeyB64))
            }
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <IconServer className="w-4 h-4" />
            {submitting ? 'Lancement…' : 'Déployer'}
          </button>
        </div>
      </form>

      {/* Liste des déploiements */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-700">Instances ({deployments.length})</h2>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800"
          >
            <IconRefresh className="w-4 h-4" /> Actualiser
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400 py-8 text-center">Chargement…</p>
        ) : deployments.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">Aucune instance déployée.</p>
        ) : (
          <div className="space-y-3">
            {deployments.map((d) => (
              <div key={d.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800">{d.org_name || d.slug}</span>
                    <span className="text-xs text-slate-400">#{d.id} · {d.slug} · {d.region}</span>
                    <StatusBadge status={d.status} />
                  </div>
                  {d.backend_url && d.status === 'ready' ? (
                    <a
                      href={d.backend_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-teal-600 hover:underline break-all"
                    >
                      {d.backend_url}
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">{d.db_node_type}</span>
                  )}
                  {d.error && <p className="text-xs text-red-600 mt-1 break-words">{d.error}</p>}
                </div>
                {d.archive_object && d.status === 'ready' && (
                  <button
                    onClick={() => doDownload(d)}
                    className="px-2.5 py-2 rounded-lg text-teal-700 bg-teal-50 hover:bg-teal-100 text-xs font-semibold"
                    title="Télécharger l'archive de déploiement (lien valable 1 h)"
                  >
                    Archive ↓
                  </button>
                )}
                {!d.archive_object && d.status === 'ready' && (
                  <button
                    onClick={() => doNotify(d)}
                    disabled={notifyingId === d.id}
                    className="p-2 rounded-lg text-teal-600 hover:bg-teal-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    title={d.notified_at ? "Renvoyer l'e-mail au client" : "Envoyer l'e-mail au client"}
                  >
                    <IconMail className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => setConfirmDelete(d)}
                  disabled={d.status === 'destroying' || d.status === 'destroyed'}
                  className="p-2 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Supprimer l'instance"
                >
                  <IconTrash className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modale de confirmation de suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
                <IconAlertTriangle className="w-6 h-6" />
              </span>
              <h3 className="text-lg font-bold text-slate-900">Supprimer l'instance ?</h3>
            </div>
            {confirmDelete.target === 'gcp' ? (
              <p className="text-sm text-slate-600">
                Le suivi du déploiement de <strong>{confirmDelete.org_name || confirmDelete.slug}</strong> sera retiré.
                La <strong>VM GCP n'est pas supprimée</strong> (clé du client non conservée) : supprimez-la dans la
                console GCP du client pour arrêter la facturation.
              </p>
            ) : SSH_TARGETS.includes(confirmDelete.target) ? (
              <p className="text-sm text-slate-600">
                Le suivi du déploiement de <strong>{confirmDelete.org_name || confirmDelete.slug}</strong> sera retiré.
                Le <strong>serveur distant n'est pas modifié</strong> (identifiants SSH non conservés) : retirez le
                conteneur avec <code>docker rm -f dashboard</code> si besoin.
              </p>
            ) : (
              <p className="text-sm text-slate-600">
                L'infrastructure Scaleway de <strong>{confirmDelete.org_name || confirmDelete.slug}</strong>{' '}
                (projet, base MySQL, conteneur) sera <strong>définitivement détruite</strong>. Les données
                de l'organisation dans la base maître ne sont pas affectées.
              </p>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Annuler
              </button>
              <button
                onClick={doDelete}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Suppression…' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
